/**
 * surfaces.ts — original procedural PBR maps and shared material factories.
 *
 * Everything here is generated in-canvas. Nothing is loaded from disk, so a
 * fresh deploy cannot 404 a texture. Materials are shared where the look is
 * identical across many meshes (flesh, stone); kit still clones per-piece so
 * each armour colour stays independent.
 */

import * as THREE from 'three';

/** Draws to an offscreen canvas, or undefined where there is no DOM. */
function paint(
  size: number,
  draw: (ctx: CanvasRenderingContext2D, size: number) => void,
): THREE.CanvasTexture | undefined {
  if (typeof document === 'undefined') return undefined;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (ctx === null) return undefined;
  draw(ctx, size);
  const texture = new THREE.CanvasTexture(canvas);
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
}

/** Linear/data map (normals, roughness) — no sRGB. */
function paintData(
  size: number,
  draw: (ctx: CanvasRenderingContext2D, size: number) => void,
): THREE.CanvasTexture | undefined {
  const texture = paint(size, draw);
  if (texture === undefined) return undefined;
  texture.colorSpace = THREE.NoColorSpace;
  return texture;
}

function hash2(x: number, y: number): number {
  const n = Math.sin(x * 127.1 + y * 311.7) * 43758.5453;
  return n - Math.floor(n);
}

function valueNoise(x: number, y: number): number {
  const x0 = Math.floor(x);
  const y0 = Math.floor(y);
  const fx = x - x0;
  const fy = y - y0;
  const ux = fx * fx * (3 - 2 * fx);
  const uy = fy * fy * (3 - 2 * fy);
  const a = hash2(x0, y0);
  const b = hash2(x0 + 1, y0);
  const c = hash2(x0, y0 + 1);
  const d = hash2(x0 + 1, y0 + 1);
  return a + (b - a) * ux + (c - a) * uy + (a - b - c + d) * ux * uy;
}

function fbm(x: number, y: number, octaves = 4): number {
  let amp = 0.5;
  let freq = 1;
  let sum = 0;
  let norm = 0;
  for (let i = 0; i < octaves; i += 1) {
    sum += amp * valueNoise(x * freq, y * freq);
    norm += amp;
    amp *= 0.5;
    freq *= 2;
  }
  return sum / norm;
}

// ---------------------------------------------------------------------------
// cached maps
// ---------------------------------------------------------------------------

let steelBump: THREE.Texture | undefined | null = null;
let steelRough: THREE.Texture | undefined | null = null;
let leatherBump: THREE.Texture | undefined | null = null;
let leatherAlbedo: THREE.Texture | undefined | null = null;
let clothWeave: THREE.Texture | undefined | null = null;
let skinBump: THREE.Texture | undefined | null = null;
let stoneAlbedo: THREE.Texture | undefined | null = null;
let stoneNormal: THREE.Texture | undefined | null = null;
let stoneAo: THREE.Texture | undefined | null = null;
let mailRings: THREE.Texture | undefined | null = null;

/**
 * Planished steel bump: hammer dishes + fine brushed grain.
 * Mid-grey is "no change" for bump maps.
 */
export function steelSurface(): THREE.Texture | undefined {
  if (steelBump !== null) return steelBump ?? undefined;
  steelBump = paintData(512, (ctx, size) => {
    ctx.fillStyle = '#808080';
    ctx.fillRect(0, 0, size, size);
    const step = 28;
    for (let y = 0; y < size + step; y += step) {
      for (let x = 0; x < size + step; x += step) {
        const jx = x + ((y / step) % 2) * (step / 2);
        const r = step * 0.48;
        const grad = ctx.createRadialGradient(jx - r * 0.28, y - r * 0.28, 1, jx, y, r);
        grad.addColorStop(0, '#949494');
        grad.addColorStop(0.5, '#808080');
        grad.addColorStop(1, '#6c6c6c');
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(jx, y, r, 0, Math.PI * 2);
        ctx.fill();
      }
    }
    for (let i = 0; i < 520; i += 1) {
      const y = Math.random() * size;
      ctx.strokeStyle = Math.random() > 0.5 ? '#8c8c8c' : '#747474';
      ctx.lineWidth = Math.random() * 1.2 + 0.25;
      ctx.beginPath();
      ctx.moveTo(Math.random() * size, y);
      ctx.lineTo(Math.random() * size, y + (Math.random() - 0.5) * 2.5);
      ctx.stroke();
    }
  });
  if (steelBump !== undefined) steelBump.repeat.set(3, 3);
  return steelBump ?? undefined;
}

/** Roughness variation for steel — brighter = rougher in our packing. */
export function steelRoughnessMap(): THREE.Texture | undefined {
  if (steelRough !== null) return steelRough ?? undefined;
  steelRough = paintData(256, (ctx, size) => {
    const img = ctx.createImageData(size, size);
    for (let y = 0; y < size; y += 1) {
      for (let x = 0; x < size; x += 1) {
        const n = fbm(x / 32, y / 32, 3);
        const v = Math.floor(110 + n * 90);
        const i = (y * size + x) * 4;
        img.data[i] = v;
        img.data[i + 1] = v;
        img.data[i + 2] = v;
        img.data[i + 3] = 255;
      }
    }
    ctx.putImageData(img, 0, 0);
  });
  if (steelRough !== undefined) steelRough.repeat.set(2.5, 2.5);
  return steelRough ?? undefined;
}

/** Leather grain bump. */
export function leatherSurface(): THREE.Texture | undefined {
  if (leatherBump !== null) return leatherBump ?? undefined;
  leatherBump = paintData(512, (ctx, size) => {
    ctx.fillStyle = '#808080';
    ctx.fillRect(0, 0, size, size);
    for (let i = 0; i < 1600; i += 1) {
      const x = Math.random() * size;
      const y = Math.random() * size;
      const r = Math.random() * 6 + 1.2;
      ctx.fillStyle = Math.random() > 0.5 ? '#909090' : '#6e6e6e';
      ctx.beginPath();
      ctx.ellipse(x, y, r, r * (0.45 + Math.random() * 0.5), Math.random() * Math.PI, 0, Math.PI * 2);
      ctx.fill();
    }
    // Crease lines.
    for (let i = 0; i < 40; i += 1) {
      ctx.strokeStyle = 'rgba(90,90,90,0.35)';
      ctx.lineWidth = 1 + Math.random() * 2;
      ctx.beginPath();
      ctx.moveTo(Math.random() * size, Math.random() * size);
      ctx.quadraticCurveTo(
        Math.random() * size,
        Math.random() * size,
        Math.random() * size,
        Math.random() * size,
      );
      ctx.stroke();
    }
  });
  if (leatherBump !== undefined) leatherBump.repeat.set(4, 4);
  return leatherBump ?? undefined;
}

/** Warm hide albedo tint variation. */
export function leatherAlbedoMap(): THREE.Texture | undefined {
  if (leatherAlbedo !== null) return leatherAlbedo ?? undefined;
  leatherAlbedo = paint(256, (ctx, size) => {
    const img = ctx.createImageData(size, size);
    for (let y = 0; y < size; y += 1) {
      for (let x = 0; x < size; x += 1) {
        const n = fbm(x / 40, y / 40, 4);
        const i = (y * size + x) * 4;
        img.data[i] = Math.floor(150 + n * 55);
        img.data[i + 1] = Math.floor(105 + n * 40);
        img.data[i + 2] = Math.floor(70 + n * 28);
        img.data[i + 3] = 255;
      }
    }
    ctx.putImageData(img, 0, 0);
  });
  if (leatherAlbedo !== undefined) leatherAlbedo.repeat.set(3, 3);
  return leatherAlbedo ?? undefined;
}

/** Cloth weave normal/bump. */
export function clothWeaveMap(): THREE.Texture | undefined {
  if (clothWeave !== null) return clothWeave ?? undefined;
  clothWeave = paintData(256, (ctx, size) => {
    ctx.fillStyle = '#808080';
    ctx.fillRect(0, 0, size, size);
    for (let y = 0; y < size; y += 3) {
      ctx.fillStyle = y % 6 === 0 ? '#8a8a8a' : '#767676';
      ctx.fillRect(0, y, size, 1);
    }
    for (let x = 0; x < size; x += 3) {
      ctx.fillStyle = x % 6 === 0 ? '#868686' : '#7a7a7a';
      ctx.globalAlpha = 0.55;
      ctx.fillRect(x, 0, 1, size);
    }
    ctx.globalAlpha = 1;
  });
  if (clothWeave !== undefined) clothWeave.repeat.set(8, 8);
  return clothWeave ?? undefined;
}

/** Soft skin pores / micro-variation. */
export function skinBumpMap(): THREE.Texture | undefined {
  if (skinBump !== null) return skinBump ?? undefined;
  skinBump = paintData(256, (ctx, size) => {
    const img = ctx.createImageData(size, size);
    for (let y = 0; y < size; y += 1) {
      for (let x = 0; x < size; x += 1) {
        const n = fbm(x / 18, y / 18, 5);
        const v = Math.floor(118 + n * 28);
        const i = (y * size + x) * 4;
        img.data[i] = v;
        img.data[i + 1] = v;
        img.data[i + 2] = v;
        img.data[i + 3] = 255;
      }
    }
    ctx.putImageData(img, 0, 0);
  });
  if (skinBump !== undefined) skinBump.repeat.set(2, 2);
  return skinBump ?? undefined;
}

/** Limestone / flagstone albedo. */
export function stoneAlbedoMap(): THREE.Texture | undefined {
  if (stoneAlbedo !== null) return stoneAlbedo ?? undefined;
  stoneAlbedo = paint(512, (ctx, size) => {
    const img = ctx.createImageData(size, size);
    for (let y = 0; y < size; y += 1) {
      for (let x = 0; x < size; x += 1) {
        const n = fbm(x / 48, y / 48, 5);
        const crack = Math.abs(fbm(x / 90, y / 90, 2) - 0.5) < 0.02 ? 0.72 : 1;
        const i = (y * size + x) * 4;
        img.data[i] = Math.floor((118 + n * 55) * crack);
        img.data[i + 1] = Math.floor((108 + n * 48) * crack);
        img.data[i + 2] = Math.floor((88 + n * 38) * crack);
        img.data[i + 3] = 255;
      }
    }
    ctx.putImageData(img, 0, 0);
  });
  if (stoneAlbedo !== undefined) stoneAlbedo.repeat.set(1.5, 1.5);
  return stoneAlbedo ?? undefined;
}

/** Stone bump packed as grayscale (used as bumpMap). */
export function stoneBumpMap(): THREE.Texture | undefined {
  if (stoneNormal !== null) return stoneNormal ?? undefined;
  stoneNormal = paintData(512, (ctx, size) => {
    const img = ctx.createImageData(size, size);
    for (let y = 0; y < size; y += 1) {
      for (let x = 0; x < size; x += 1) {
        const n = fbm(x / 36, y / 36, 5);
        const v = Math.floor(100 + n * 80);
        const i = (y * size + x) * 4;
        img.data[i] = v;
        img.data[i + 1] = v;
        img.data[i + 2] = v;
        img.data[i + 3] = 255;
      }
    }
    ctx.putImageData(img, 0, 0);
  });
  if (stoneNormal !== undefined) stoneNormal.repeat.set(1.5, 1.5);
  return stoneNormal ?? undefined;
}

/** Soft AO / dirt in crevices for stone. */
export function stoneAoMap(): THREE.Texture | undefined {
  if (stoneAo !== null) return stoneAo ?? undefined;
  stoneAo = paintData(256, (ctx, size) => {
    const img = ctx.createImageData(size, size);
    for (let y = 0; y < size; y += 1) {
      for (let x = 0; x < size; x += 1) {
        const edge = Math.min(x, y, size - 1 - x, size - 1 - y) / (size * 0.08);
        const n = fbm(x / 40, y / 40, 3);
        const v = Math.floor(Math.min(255, (0.55 + 0.45 * Math.min(1, edge) + n * 0.15) * 255));
        const i = (y * size + x) * 4;
        img.data[i] = v;
        img.data[i + 1] = v;
        img.data[i + 2] = v;
        img.data[i + 3] = 255;
      }
    }
    ctx.putImageData(img, 0, 0);
  });
  return stoneAo ?? undefined;
}

/** Mail ring pattern (albedo + bump). */
export function mailRingMap(): THREE.Texture | undefined {
  if (mailRings !== null) return mailRings ?? undefined;
  mailRings = paint(256, (ctx, size) => {
    ctx.fillStyle = '#6a7078';
    ctx.fillRect(0, 0, size, size);
    const step = 10;
    for (let y = 0; y < size + step; y += step) {
      const odd = Math.floor(y / step) % 2;
      for (let x = odd * (step / 2); x < size + step; x += step) {
        ctx.strokeStyle = '#9aa3ad';
        ctx.lineWidth = 1.6;
        ctx.beginPath();
        ctx.arc(x, y, step * 0.38, 0, Math.PI * 2);
        ctx.stroke();
        ctx.strokeStyle = '#4a5058';
        ctx.lineWidth = 0.8;
        ctx.beginPath();
        ctx.arc(x + 1, y + 1, step * 0.38, 0, Math.PI * 2);
        ctx.stroke();
      }
    }
  });
  if (mailRings !== undefined) mailRings.repeat.set(5, 5);
  return mailRings ?? undefined;
}

// ---------------------------------------------------------------------------
// material factories
// ---------------------------------------------------------------------------

export interface SurfaceMaterials {
  flesh: THREE.MeshStandardMaterial;
  cloth: THREE.MeshStandardMaterial;
  darkCloth: THREE.MeshStandardMaterial;
  team: THREE.MeshStandardMaterial;
  steel: THREE.MeshStandardMaterial;
  leather: THREE.MeshStandardMaterial;
  stone: THREE.MeshStandardMaterial;
  mortar: THREE.MeshStandardMaterial;
  iron: THREE.MeshStandardMaterial;
  disposables: { dispose(): void }[];
}

/**
 * Shared body/arena materials for one figure or room. Caller owns disposal of
 * the returned materials (textures are module-cached and must NOT be disposed).
 */
export function createFleshMaterial(): THREE.MeshStandardMaterial {
  const mat = new THREE.MeshStandardMaterial({
    color: 0xc9a084,
    roughness: 0.72,
    metalness: 0.02,
  });
  const bump = skinBumpMap();
  if (bump !== undefined) {
    mat.bumpMap = bump;
    mat.bumpScale = 0.018;
  }
  return mat;
}

export function createClothMaterial(color: number): THREE.MeshStandardMaterial {
  const mat = new THREE.MeshStandardMaterial({
    color,
    roughness: 0.88,
    metalness: 0.02,
  });
  const weave = clothWeaveMap();
  if (weave !== undefined) {
    mat.bumpMap = weave;
    mat.bumpScale = 0.04;
  }
  return mat;
}

export function createSteelMaterial(color: number, opts?: { heavy?: boolean }): THREE.MeshStandardMaterial {
  const heavy = opts?.heavy ?? true;
  const mat = new THREE.MeshStandardMaterial({
    color,
    metalness: heavy ? 0.92 : 0.78,
    roughness: heavy ? 0.38 : 0.55,
    envMapIntensity: heavy ? 1.05 : 0.75,
  });
  const bump = steelSurface();
  if (bump !== undefined) {
    mat.bumpMap = bump;
    mat.bumpScale = 0.32;
  }
  const rough = steelRoughnessMap();
  if (rough !== undefined) mat.roughnessMap = rough;
  return mat;
}

export function createLeatherMaterial(color: number): THREE.MeshStandardMaterial {
  const mat = new THREE.MeshStandardMaterial({
    color,
    roughness: 0.86,
    metalness: 0.04,
    envMapIntensity: 0.35,
  });
  const albedo = leatherAlbedoMap();
  if (albedo !== undefined) {
    mat.map = albedo;
    mat.color.multiply(new THREE.Color(color));
  }
  const bump = leatherSurface();
  if (bump !== undefined) {
    mat.bumpMap = bump;
    mat.bumpScale = 0.55;
  }
  return mat;
}

export function createStoneMaterial(tint = 0x8b8172): THREE.MeshStandardMaterial {
  const mat = new THREE.MeshStandardMaterial({
    color: tint,
    roughness: 0.92,
    metalness: 0.03,
  });
  const albedo = stoneAlbedoMap();
  if (albedo !== undefined) mat.map = albedo;
  const bump = stoneBumpMap();
  if (bump !== undefined) {
    mat.bumpMap = bump;
    mat.bumpScale = 0.45;
  }
  const ao = stoneAoMap();
  if (ao !== undefined) mat.aoMap = ao;
  return mat;
}

export function createIronMaterial(): THREE.MeshStandardMaterial {
  const mat = new THREE.MeshStandardMaterial({
    color: 0x3a3530,
    metalness: 0.72,
    roughness: 0.55,
    envMapIntensity: 0.7,
  });
  const bump = steelSurface();
  if (bump !== undefined) {
    mat.bumpMap = bump;
    mat.bumpScale = 0.5;
  }
  return mat;
}

/** Apply kit-class maps onto an existing main material (heavy / mail / light). */
export function applyKitSurface(
  main: THREE.MeshStandardMaterial,
  kind: 'heavy' | 'medium' | 'light',
): void {
  if (kind === 'heavy') {
    const bump = steelSurface();
    if (bump !== undefined) {
      main.bumpMap = bump;
      main.bumpScale = 0.35;
    }
    const rough = steelRoughnessMap();
    if (rough !== undefined) main.roughnessMap = rough;
    main.metalness = Math.max(main.metalness, 0.9);
    main.envMapIntensity = Math.max(main.envMapIntensity, 0.95);
  } else if (kind === 'medium') {
    const texture = mailRingMap();
    if (texture !== undefined) {
      main.map = texture;
      main.bumpMap = texture;
      main.bumpScale = 1.05;
    }
  } else {
    const bump = leatherSurface();
    if (bump !== undefined) {
      main.bumpMap = bump;
      main.bumpScale = 0.6;
    }
    const albedo = leatherAlbedoMap();
    if (albedo !== undefined && main.map === null) {
      main.map = albedo;
    }
  }
}

/**
 * Beveled box with more segments — reads as dressed stone rather than a Minecraft block.
 */
export function beveledBoxGeometry(
  w: number,
  h: number,
  d: number,
  bevel = 0.02,
  segments = 2,
): THREE.BufferGeometry {
  const shape = new THREE.Shape();
  const hw = w / 2 - bevel;
  const hd = d / 2 - bevel;
  shape.moveTo(-hw, -hd);
  shape.lineTo(hw, -hd);
  shape.lineTo(hw, hd);
  shape.lineTo(-hw, hd);
  shape.closePath();
  const geo = new THREE.ExtrudeGeometry(shape, {
    depth: h,
    bevelEnabled: true,
    bevelThickness: bevel,
    bevelSize: bevel,
    bevelSegments: Math.max(1, segments),
    curveSegments: 4,
  });
  // Extrude along Z; rotate so height is Y.
  geo.rotateX(-Math.PI / 2);
  geo.translate(0, h / 2, 0);
  geo.computeVertexNormals();
  return geo;
}

/**
 * Dense capsule with anatomical taper along Y. Built at LIMB_UNIT so posing
 * stretch still works. `tipKeep` < 1 narrows toward +Y.
 */
export function denseLimbGeometry(
  radius: number,
  length = 0.4,
  opts?: { radial?: number; tipKeep?: number; wide?: number; deep?: number },
): THREE.CapsuleGeometry {
  const radial = opts?.radial ?? 14;
  const geo = new THREE.CapsuleGeometry(radius, length, 6, radial);
  const tipKeep = opts?.tipKeep ?? 1;
  if (tipKeep !== 1) {
    const pos = geo.attributes['position'] as THREE.BufferAttribute;
    geo.computeBoundingBox();
    const box = geo.boundingBox!;
    const span = box.max.y - box.min.y || 1;
    for (let i = 0; i < pos.count; i += 1) {
      const y = pos.getY(i);
      const t = (y - box.min.y) / span;
      const scale = 1 - (1 - tipKeep) * t;
      pos.setX(i, pos.getX(i) * scale);
      pos.setZ(i, pos.getZ(i) * scale);
    }
    pos.needsUpdate = true;
  }
  if (opts?.wide !== undefined || opts?.deep !== undefined) {
    geo.scale(opts.wide ?? 1, 1, opts.deep ?? 1);
  }
  geo.computeVertexNormals();
  return geo;
}

/** Tag a mesh as near-only detail for LOD. Tiny casters are skipped in shadows. */
export function markNearDetail(object: THREE.Object3D): void {
  object.userData['lod'] = 'near';
  object.traverse((obj) => {
    const mesh = obj as THREE.Mesh;
    if (mesh.isMesh) mesh.castShadow = false;
  });
}

/**
 * Toggle near-only detail. Skips the traverse when the desired state matches
 * the last applied value on this root (LOD used to walk every figure every frame).
 */
export function setNearDetailVisible(root: THREE.Object3D, visible: boolean): void {
  if (root.userData['lodNear'] === visible) return;
  root.userData['lodNear'] = visible;
  root.traverse((obj) => {
    if (obj.userData['lod'] !== 'near') return;
    // Kit hiding wins over LOD. A face part a helm covers must stay hidden even
    // when it is close enough to count as near-detail, or the flesh brow and
    // eye sockets show through the visor. `wearKit` marks the parts it hides and
    // clears `lodNear` so this pass recomputes against the current kit.
    obj.visible = visible && obj.userData['hiddenByKit'] !== true;
  });
}
